import {
  Dispatch,
  ReactElement,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { PaymentMethodDisplay } from "checkout-experience/components/payment-method/payment-method-summary-display";
import {
  BrowserSettings,
  ExperienceType,
  PaymentMessages,
  PaymentRequirements,
} from "checkout-query-client/api/types";
import { CheckoutSession } from "checkout-query-client/hooks/use-checkout-session";

import {
  GetMerchantSettingsResponse_MerchantSettings,
  PlainMessage,
} from "frontend-client";

export type AppContextWrapperProps = {
  children: ReactElement;
  value: AppContextType;
};

export type AuthorizePayloadType = {
  ACCESS_PRIVILEGE: string;
  MIDS: string[];
};

export type MfePropsType = {
  authToken: string;
  mfeApp: string;
  product: string;
  authorizePayload?: AuthorizePayloadType;
};

export type AppContextType = {
  paymentSummary?: PaymentMethodDisplay;
  setPaymentSummary?: Dispatch<
    SetStateAction<PaymentMethodDisplay | undefined>
  >;
  checkoutSessionToken: string;
  setCheckoutSessionToken?: Dispatch<SetStateAction<string>>;
  experienceType?: ExperienceType;
  setExperienceType?: Dispatch<SetStateAction<ExperienceType>>;
  apiHost: string;
  version: string;
  target: string;
  kountSessionHostname: string;

  paymentRequirements: PaymentRequirements;
  setPaymentRequirements?: Dispatch<SetStateAction<PaymentRequirements>>;
  paymentMessages: PaymentMessages;
  setPaymentMessages?: Dispatch<SetStateAction<PaymentMessages>>;
  browserSettings?: BrowserSettings;
  setBrowserSettings?: Dispatch<SetStateAction<BrowserSettings>>;

  mppApiHost?: string;
  callMppApi?: boolean;

  merchantId?: string;
  setMerchantId?: Dispatch<SetStateAction<string>>;

  segmentId?: string;
  setSegmentId?: Dispatch<SetStateAction<string>>;

  sampleCheckoutSession?: CheckoutSession;
  setSampleCheckoutSession?: Dispatch<
    SetStateAction<CheckoutSession | undefined>
  >;
  msApiRoot?: string;
  googlePayEnv?: string;

  sampleMerchantSettings?: PlainMessage<GetMerchantSettingsResponse_MerchantSettings>;
  controlledSubmit?: boolean;

  pazeSDK?: string;
  deviceFingerprintSDK?: string;
  mfeProps?: MfePropsType;
};

export const AppContext = createContext<AppContextType>({
  checkoutSessionToken: "",
  experienceType: "dropInUI",
  apiHost: "",
  version: "",
  target: "",
  kountSessionHostname: "",
  paymentRequirements: {},
  paymentMessages: {},
  browserSettings: {},
  googlePayEnv: "",
  pazeSDK: "",
  deviceFingerprintSDK: "",
});

export const AppContextWrapper = ({
  children,
  value,
}: AppContextWrapperProps) => {
  const [paymentSummary, setPaymentSummary] = useState<
    PaymentMethodDisplay | undefined
  >(value.paymentSummary ?? undefined);
  const [checkoutSessionToken, setCheckoutSessionToken] = useState(
    value.checkoutSessionToken ?? ""
  );
  const [segmentId, setSegmentId] = useState(value.segmentId ?? "");
  const [merchantId, setMerchantId] = useState(value.merchantId ?? "");
  const [experienceType, setExperienceType] = useState<ExperienceType>(
    value.experienceType ?? "dropInUI"
  );

  const [sampleCheckoutSession, setSampleCheckoutSession] = useState<
    CheckoutSession | undefined
  >(value.sampleCheckoutSession);

  const [paymentRequirements, setPaymentRequirements] =
    useState<PaymentRequirements>(value.paymentRequirements ?? {});

  const [paymentMessages, setPaymentMessages] = useState<PaymentMessages>(
    value.paymentMessages ?? {}
  );

  const [browserSettings, setBrowserSettings] = useState<BrowserSettings>({});

  const finalValue = useMemo(() => {
    return {
      ...value,
      checkoutSessionToken,
      setCheckoutSessionToken,
      experienceType,
      setExperienceType,
      paymentRequirements,
      setPaymentRequirements,
      paymentMessages,
      setPaymentMessages,
      browserSettings,
      setBrowserSettings,
      merchantId,
      setMerchantId,
      segmentId,
      setSegmentId,
      setSampleCheckoutSession,
      sampleCheckoutSession,
      paymentSummary,
      setPaymentSummary,
    };
  }, [
    value,
    checkoutSessionToken,
    experienceType,
    paymentRequirements,
    paymentMessages,
    browserSettings,
    sampleCheckoutSession,
    merchantId,
    paymentSummary,
    segmentId,
  ]);

  useEffect(() => {
    if (
      value.checkoutSessionToken &&
      value.checkoutSessionToken !== checkoutSessionToken
    ) {
      setCheckoutSessionToken(value.checkoutSessionToken);
    }
  }, [value.checkoutSessionToken]);

  useEffect(() => {
    if (value.experienceType && value.experienceType !== experienceType) {
      setExperienceType(value.experienceType);
    }
  }, [value.experienceType]);

  useEffect(() => {
    if (
      JSON.stringify(value.sampleCheckoutSession) !==
      JSON.stringify(sampleCheckoutSession)
    ) {
      setSampleCheckoutSession(value.sampleCheckoutSession);
    }
  }, [value.sampleCheckoutSession]);

  useEffect(() => {
    if (
      JSON.stringify(value.paymentRequirements) !==
      JSON.stringify(paymentRequirements)
    ) {
      setPaymentRequirements(value.paymentRequirements);
    }
  }, [value.paymentRequirements]);

  useEffect(() => {
    if (
      JSON.stringify(value.paymentMessages) !== JSON.stringify(paymentMessages)
    ) {
      setPaymentMessages(value.paymentMessages);
    }
  }, [value.paymentMessages]);
  return (
    <AppContext.Provider value={finalValue}>{children}</AppContext.Provider>
  );
};

export const useAppContext = () => {
  return useContext(AppContext);
};
